/*
Copyright (C) 2026  Integrated Tech9labs Pvt. Ltd.
This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.
This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
GNU Affero General Public License for more details.
You should have received a copy of the GNU Affero General Public License
along with this program.  If not, see <http://www.gnu.org/licenses/>.
*/

import { MIB } from "@src/utils/TransferProgress";

/* The geometry and pacing of the MigrationFlow animation, kept apart so it is plain maths. */

export type Pt = { x: number; y: number };
export type LutPoint = { x: number; y: number; angle: number };

const cubic = (p0: Pt, p1: Pt, p2: Pt, p3: Pt, t: number): Pt => {
  const u = 1 - t;
  const a = u * u * u;
  const b = 3 * u * u * t;
  const c = 3 * u * t * t;
  const d = t * t * t;
  return {
    x: a * p0.x + b * p1.x + c * p2.x + d * p3.x,
    y: a * p0.y + b * p1.y + c * p2.y + d * p3.y,
  };
};

/**
 * Points evenly spaced by distance along a cubic Bézier (so packets move at a constant speed),
 * each with the direction of travel in degrees. Returns the table and the curve's length.
 */
export const buildLut = (p0: Pt, p1: Pt, p2: Pt, p3: Pt, samples = 360) => {
  const fine = samples * 8;
  const pts: Pt[] = [];
  const dist: number[] = [0];
  for (let i = 0; i <= fine; i += 1) {
    pts.push(cubic(p0, p1, p2, p3, i / fine));
    if (i > 0) {
      const dx = pts[i].x - pts[i - 1].x;
      const dy = pts[i].y - pts[i - 1].y;
      dist.push(dist[i - 1] + Math.sqrt(dx * dx + dy * dy));
    }
  }
  const length = dist[fine];
  const lut: LutPoint[] = [];
  let j = 0;
  for (let i = 0; i <= samples; i += 1) {
    const target = (i / samples) * length;
    while (j < fine && dist[j + 1] < target) {
      j += 1;
    }
    const span = dist[j + 1] - dist[j] || 1;
    const f = Math.max(0, Math.min(1, (target - dist[j]) / span));
    const a = pts[j];
    const b = pts[Math.min(fine, j + 1)];
    lut.push({ x: a.x + (b.x - a.x) * f, y: a.y + (b.y - a.y) * f, angle: 0 });
  }
  lut.forEach((p, i) => {
    const prev = lut[Math.max(0, i - 1)];
    const next = lut[Math.min(lut.length - 1, i + 1)];
    p.angle = (Math.atan2(next.y - prev.y, next.x - prev.x) * 180) / Math.PI;
  });
  return { lut, length };
};

/** The point at `f` (0..1) of the way along a table from buildLut. */
export const pointAt = (lut: LutPoint[], f: number): LutPoint => {
  const clamped = Math.max(0, Math.min(1, f));
  return lut[Math.round(clamped * (lut.length - 1))];
};

const log2 = (x: number) => Math.log(x) / Math.LN2;

/** Packet speed in px/s (viewBox units) for a throughput: faster with more data, never frantic. */
export const packetSpeed = (bytesPerSec: number) => {
  if (!bytesPerSec || bytesPerSec <= 0) {
    return 0;
  }
  const mib = bytesPerSec / MIB;
  return Math.max(70, Math.min(380, 70 + 55 * log2(1 + mib / 4)));
};

/** How many packets are on the link at once: denser with more data and more disks copying. */
export const packetCount = (bytesPerSec: number, activeDisks = 1, max = 9) => {
  if (!bytesPerSec || bytesPerSec <= 0) {
    return 0;
  }
  const mib = bytesPerSec / MIB;
  const n = Math.round(2 + 0.9 * log2(1 + mib / 8) + Math.max(0, activeDisks - 1) * 0.7);
  return Math.max(2, Math.min(max, n));
};

/** Which of `slots` evenly spaced slots carry a packet when `count` are shown (spread evenly). */
export const slotVisible = (slot: number, count: number, slots: number) => {
  if (count <= 0) {
    return false;
  }
  if (count >= slots) {
    return true;
  }
  return Math.floor((slot * count) / slots) !== Math.floor(((slot - 1) * count) / slots) || slot === 0;
};
